import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SignaturePad from '../../HR/Forms/SignaturePad';
import {
  initialsFromName,
  renderTypedSignature,
  trimSignature,
  SCRIPT_FONT_FAMILY,
} from './signatureImage';

/**
 * Signer adopts a signature and initials, typed in a script font or drawn.
 * onAdopt({ signatureImage, initialsImage, signatureMethod, adoptedName, adoptedInitials })
 */
const AdoptSignatureDialog = ({ open, defaultName, needsInitials, onAdopt, onClose }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [method, setMethod] = useState('type');
  const [name, setName] = useState(defaultName || '');
  const [initials, setInitials] = useState(initialsFromName(defaultName));
  const [drawnSignature, setDrawnSignature] = useState(null);
  const [drawnInitials, setDrawnInitials] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName || '');
      setInitials(initialsFromName(defaultName));
      setError('');
    }
  }, [open, defaultName]);

  const handleAdopt = async () => {
    setError('');
    setBusy(true);
    try {
      let signatureImage;
      let initialsImage = null;
      if (method === 'type') {
        signatureImage = await renderTypedSignature(name);
        if (needsInitials) initialsImage = await renderTypedSignature(initials);
      } else {
        signatureImage = await trimSignature(drawnSignature);
        if (needsInitials) initialsImage = await trimSignature(drawnInitials);
      }
      if (!signatureImage || (needsInitials && !initialsImage)) {
        setError(t('esign.sign.adoptMissing'));
        return;
      }
      onAdopt({
        signatureImage,
        initialsImage,
        signatureMethod: method,
        adoptedName: name.trim(),
        adoptedInitials: initials.trim(),
      });
    } catch (e) {
      setError(t('esign.sign.error'));
    } finally {
      setBusy(false);
    }
  };

  const padLanguage = String(i18n.language || '').startsWith('es') ? 'es' : 'en';

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>{t('esign.sign.adoptTitle')}</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={method}
          onChange={(_, v) => {
            // The pads remount empty, so forget anything drawn before.
            setDrawnSignature(null);
            setDrawnInitials(null);
            setMethod(v);
          }}
          sx={{ mb: 2 }}
        >
          <Tab value="type" label={t('esign.sign.type')} />
          <Tab value="draw" label={t('esign.sign.draw')} />
        </Tabs>

        {method === 'type' ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('esign.sign.fullName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 80 }}
              />
              {needsInitials && (
                <TextField
                  label={t('esign.sign.initials')}
                  value={initials}
                  onChange={(e) => setInitials(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                  inputProps={{ maxLength: 5 }}
                />
              )}
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('esign.sign.preview')}</Typography>
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'grey.400',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 4,
                  flexWrap: 'wrap',
                  minHeight: 80,
                  fontFamily: SCRIPT_FONT_FAMILY,
                  fontWeight: 600,
                  color: '#0b1a5a',
                }}
              >
                <Box component="span" sx={{ fontSize: 40, lineHeight: 1.2 }}>{name}</Box>
                {needsInitials && <Box component="span" sx={{ fontSize: 34, lineHeight: 1.2 }}>{initials}</Box>}
              </Box>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <TextField
              label={t('esign.sign.fullName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 80 }}
            />
            <SignaturePad label={t('esign.sign.drawSignature')} language={padLanguage} onSignature={setDrawnSignature} />
            {needsInitials && (
              <Box sx={{ maxWidth: 320 }}>
                <SignaturePad label={t('esign.sign.drawInitials')} language={padLanguage} onSignature={setDrawnInitials} />
              </Box>
            )}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
          {t('esign.sign.adoptNote')}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('esign.sign.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleAdopt}
          disabled={busy || !name.trim() || (method === 'type' && needsInitials && !initials.trim())}
        >
          {t('esign.sign.adopt')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdoptSignatureDialog;
